import { QueryClient } from '@tanstack/react-query';

import {
  BloodReportMetadata,
  BloodReportPanel,
  PatchBloodReport,
  PatchBloodReportPanel,
} from '@/api/model';

export type BloodReportMetadataQueryKey = readonly [metadataKey: string, carePlanId: string];

export interface PatchBloodReportOptimisticContext {
  previousMetadata: BloodReportMetadata | undefined;
}

export async function cancelAndSnapshotBloodReportMetadata({
  queryClient,
  queryKey,
}: {
  queryClient: QueryClient;
  queryKey: BloodReportMetadataQueryKey;
}): Promise<PatchBloodReportOptimisticContext> {
  await queryClient.cancelQueries({ queryKey });
  const previousMetadata = queryClient.getQueryData<BloodReportMetadata>(queryKey);

  return { previousMetadata };
}

export function rollbackBloodReportMetadataFromContext({
  context,
  queryClient,
  queryKey,
}: {
  context: PatchBloodReportOptimisticContext | undefined;
  queryClient: QueryClient;
  queryKey: BloodReportMetadataQueryKey;
}): void {
  if (context?.previousMetadata) {
    queryClient.setQueryData<BloodReportMetadata>(queryKey, context.previousMetadata);
  }
}

export function applyOptimisticBloodReportPatch(
  previous: BloodReportMetadata,
  patch: PatchBloodReport,
): BloodReportMetadata {
  return { ...previous, ...patch };
}

export function applyOptimisticBloodReportPanelPatch({
  panelId,
  patch,
  previous,
}: {
  panelId: string;
  patch: PatchBloodReportPanel;
  previous: BloodReportMetadata;
}): BloodReportMetadata {
  return {
    ...previous,
    panels: previous.panels.map((panel) =>
      panel.panelId === panelId ? { ...panel, ...patch } : panel,
    ),
  };
}

export function mergeBloodReportPanelIntoMetadata(
  metadata: BloodReportMetadata,
  panel: BloodReportPanel,
): BloodReportMetadata {
  return {
    ...metadata,
    panels: metadata.panels.map((metaDataPanel) =>
      metaDataPanel.panelId === panel.panelId ? panel : metaDataPanel,
    ),
  };
}
